import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { AuthService } from '@klickdata/core/auth';
import { MessageService } from '@klickdata/core/message';
import { MobileService } from '@klickdata/core/mobile';
import { AppScope, OccasionsDates, Resource, ResourceTypes } from '@klickdata/core/resource';
import { ResourcePlayabilityService } from '@klickdata/core/resource/src/resource-playability.service';
import { PlayableResourceSpec } from '@klickdata/core/resource/src/resource.model';
import {
    Filter,
    GlobalFilterProperty,
    SelectFilterOption,
    TableFilterComponent,
    TableSource,
} from '@klickdata/core/table';
import { User, UserActionHistory } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { LocalStorage } from 'ngx-webstorage';
import { BehaviorSubject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';
import { ResourceEnrollmentSheetComponent } from '../../resource-assignment/resource-enrollment-sheet/resource-enrollment-sheet.component';
import { OccasionsDatesListingService } from './occasions-dates-listing.service';

@Component({
    selector: 'app-instructor-enrollments-occasions-listing',
    templateUrl: './instructor-enrollments-occasions-listing.component.html',
    styleUrls: ['./instructor-enrollments-occasions-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OccasionsDatesListingService],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class InstructorEnrollmentsOccasionsListingComponent extends OnDestroyHandler implements OnInit, AfterViewInit {
    public dataSource = new TableSource<OccasionsDates>();
    @LocalStorage(UserActionHistory.SELECTED_HOME_TABS_RESOURCE_OCCASION_ID, null)
    expandedElement: number | null;
    public accordionExoanded: boolean;
    public columns = ['enrollment_date', 'occasion_date', 'enrollment', 'approved', 'dismissed', 'pending', 'tools'];
    public isMobile: boolean;
    AppScope = AppScope;
    @Input() resource: Resource;
    @Input() filtersToBeApplied: Filter<any>[] = [];
    @Input() user: User;
    @Input() showAUContext: boolean = true;
    @Output() onUpdateStatus: EventEmitter<void> = new EventEmitter<void>();
    @Output() onEnrollMyself: EventEmitter<{
        occasion: OccasionsDates;
        resource: Resource;
        playableResourceCheck: PlayableResourceSpec;
    }> = new EventEmitter<{
        occasion: OccasionsDates;
        resource: Resource;
        playableResourceCheck: PlayableResourceSpec;
    }>();
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    ResourceTypes = ResourceTypes;
    public approvalStatusOptions: SelectFilterOption[];
    GlobalFilterProperty = GlobalFilterProperty;
    public totalOccasionCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    constructor(
        protected listingService: OccasionsDatesListingService,
        protected mobile: MobileService,
        protected dialog: MatDialog,
        protected auth: AuthService,
        protected cdRef: ChangeDetectorRef,
        protected messageService: MessageService,
        protected bottomSheet: MatBottomSheet,
        protected resourcePlayabilityService: ResourcePlayabilityService
    ) {
        super();
    }

    public ngOnInit() {
        this.mobile
            .isMobile()
            .pipe(takeUntil(this.destroy), shareReplay())
            .subscribe((isMobile) => (this.isMobile = isMobile));
        this.approvalStatusOptions = Utils.getResApprovalStatusOptions();
        this.dataSource.latestData
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.totalOccasionCount$.next(this.dataSource.length));
        if (!this.user.isAdmin()) {
            this.columns = ['enrollment_date', 'occasion_date', 'enrollment_count', 'tools'];
        }
    }
    ngAfterViewInit(): void {
        this.dataSource.service = this.listingService;
        this.dataSource.filter = this.filter;
        this.dataSource.paginator = this.paginator;
        this.filter.createOrUpdateFilter([
            ...this.filtersToBeApplied,
            { property: 'resource', items: [this.resource.id] },
        ]);
    }
    addEnrollment(occasion: any, me: boolean = false) {
        let data: any = {
            occId: occasion.id,
            resource: this.resource,
            customer_id: this.resource.customer_id,
        };
        if (me) {
            data = {
                ...data,
                user: this.user,
            };
        }
        this.bottomSheet
            .open(ResourceEnrollmentSheetComponent, {
                data: data,
                panelClass: 'sheet-wrapper',
            })
            .afterDismissed()
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (res) {
                    this.onUpdateStatus.emit();
                }
            });
    }
    isLastRow(occasion: OccasionsDates): boolean {
        const data = this.dataSource.data;
        return data.length > 0 && data[data.length - 1].id === occasion.id;
    }
}
